import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useAuth } from '../../contexts/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface EnrollmentData {
  [month: string]: number;
}

const EnrollmentTimelineChart: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<EnrollmentData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchEnrollmentData();
  }, []);

  const fetchEnrollmentData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/charts/enrollment-timeline', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch enrollment data: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching enrollment data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Sort months chronologically
  const sortMonths = (months: string[]) => {
    return months.sort((a, b) => {
      // Extract year and month from "Mon YYYY" format
      const [monthA, yearA] = a.split(' ');
      const [monthB, yearB] = b.split(' ');
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const dateA = new Date(parseInt(yearA), monthNames.indexOf(monthA));
      const dateB = new Date(parseInt(yearB), monthNames.indexOf(monthB));
      
      return dateA.getTime() - dateB.getTime();
    });
  };

  const sortedMonths = sortMonths(Object.keys(data));

  const chartData = {
    labels: sortedMonths,
    datasets: [
      {
        label: 'Students Added',
        data: sortedMonths.map(month => data[month]),
        fill: true,
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        borderColor: 'rgba(75, 192, 192, 1)',
        pointBackgroundColor: 'rgba(75, 192, 192, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        borderWidth: 3,
        tension: 0.4, // Smooth curve
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Student Enrollment Timeline (Last 12 Months)',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        color: '#2c3e50',
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            const studentCount = context.raw;
            const studentText = studentCount === 1 ? 'student' : 'students';
            return `${studentCount} ${studentText} added`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#666',
          font: {
            size: 12,
          }
        },
        grid: {
          color: '#f0f0f0',
          drawBorder: false,
        },
        title: {
          display: true,
          text: 'Number of Students',
          color: '#666',
          font: {
            size: 14,
            weight: 'bold' as const,
          }
        }
      },
      x: {
        ticks: {
          color: '#666',
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 0,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
        title: {
          display: true,
          text: 'Month',
          color: '#666',
          font: {
            size: 14,
            weight: 'bold' as const,
          }
        }
      },
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutCubic' as const,
      delay: (context: any) => context.dataIndex * 100,
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    elements: {
      point: {
        hoverBackgroundColor: 'rgba(75, 192, 192, 1)',
        hoverBorderColor: '#fff',
        hoverBorderWidth: 3,
      }
    }
  };

  if (loading) {
    return (
      <div className="chart-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading enrollment timeline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-error">
        <p>Error loading chart: {error}</p>
        <button onClick={fetchEnrollmentData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (Object.keys(data).length === 0) {
    return (
      <div className="chart-empty">
        <p>No enrollment data available</p>
        <p className="chart-empty-detail">Student enrollment history will appear here</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-wrapper">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default EnrollmentTimelineChart;
